import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import { useLocation } from "react-router-dom";
import FormNavigation from "./ServiceSelector/shared/FormNavigation";
import StepIndicator from "./ServiceSelector/shared/StepIndicator";
import FormContainer from "./ServiceSelector/shared/FormContainer";
import FormHeader from "./ServiceSelector/shared/FormHeader";
import TextInput from "./ServiceSelector/shared/TextInput";
import TextArea from "./ServiceSelector/shared/TextArea";
import SelectDropdown from "./ServiceSelector/shared/SelectDropdown";
import AgeGroupSelector from "./ServiceSelector/shared/AgeGroupSelector";

export default function ServiceSelector() {
  const searchParams = new URLSearchParams(window.location.search);
  const editMode = searchParams.get("edit") === "true";

  const [step, setStep] = useState("service");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [loading, setLoading] = useState(editMode);

  const [formData, setFormData] = useState({
    name: "",
    church: "",
    churchCity: "",
    denomination: "",
    qualifications: "",
    bio: "",
    profilePictureUrl: "",
    title: "",
    customServiceType: "",
    topics: [],
    instruments: [],
    description: "",
    ageGroups: [], // Target age groups for ministry
    typicalSessionLength: "", // MOVED FROM STEP 4
    travelDistance: "",
    travelUnit: "miles",
    otherConsiderations: "",
    location: "",
    // Availability preferences
    onlineAvailable: false,
    inPersonAvailable: false,
    // New travel preference fields
    travelMethod: [], // Array: ['car'], ['train'], or ['car', 'train']
    carLimitType: "", // 'distance' or 'time'
    maxCarDistance: "",
    maxCarTime: "",
    maxTrainTime: "",
    maxTrainChanges: "",
    availabilityType: "", // NEW: 'weekdays', 'weekends', 'evenings', etc.
    minAdvanceNoticeDays: "", // NEW
    availabilityNotes: "", // NEW
    pricingModel: "",
    expensesNeeded: {
      travel: false,
      accommodation: false,
      equipment: false,
      meals: false,
    },
    honorariumAccepted: null,
    suggestedContribution: "",
    minimumRate: "",
    sessionRate: "",
    sessionDuration: "",
    rateIncludes: {
      travelIncluded: false,
      equipmentIncluded: false,
      preparationIncluded: false,
      qaIncluded: false,
    },
    negotiable: null,
    selectedDays: [],
    marketingConsent: true,
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [currentTopic, setCurrentTopic] = useState("");
  const [currentInstrument, setCurrentInstrument] = useState("");
  const [currentTimeSlot, setCurrentTimeSlot] = useState({
    day: "",
    startTime: "",
    endTime: "",
  });
  const [currentAudienceTier, setCurrentAudienceTier] = useState({
    range: "",
    price: "",
  });

  // ADD THIS ENTIRE BLOCK:
  useEffect(() => {
    if (editMode) {
      loadExistingProfile();
    }
  }, [editMode]);

  const loadExistingProfile = async () => {
    try {
      setLoading(true);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("You must be logged in");
        window.location.href = "/";
        return;
      }

      // Get profile data
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (profileError) throw profileError;

      // Get expertise areas (topics for speakers)
      const { data: expertise, error: expertiseError } = await supabase
        .from("speaker_expertise")
        .select("expertise_area")
        .eq("user_id", user.id);

      if (expertiseError) throw expertiseError;

      // Get instruments (for musicians)
      const { data: instruments, error: instrumentsError } = await supabase
        .from("musician_expertise")
        .select("instrument")
        .eq("user_id", user.id);

      // Don't throw error if table doesn't exist yet
      if (
        instrumentsError &&
        !instrumentsError.message.includes("does not exist")
      ) {
        throw instrumentsError;
      }

      // Pre-populate form
      setSelectedTypes(profile.service_types || []);
      setStep("service");

      // Set image preview if profile picture exists
      if (profile.profile_picture_url) {
        setImagePreview(profile.profile_picture_url);
      }

      setFormData({
        name: profile.full_name || "",
        church: profile.church_name || "",
        churchCity: profile.church_city || "",
        denomination: profile.denomination || "",
        qualifications: profile.qualifications || "",
        bio: profile.bio2 || "",
        profilePictureUrl: profile.profile_picture_url || "",
        title: profile.bio1 || "",
        customServiceType: profile.custom_service_type || "",
        topics: expertise?.map((e) => e.expertise_area) || [],
        instruments: instruments?.map((i) => i.instrument) || [],
        description: profile.service_description || "",
        ageGroups: profile.age_groups || [],
        typicalSessionLength: profile.preferred_duration?.includes("minutes")
          ? profile.preferred_duration.replace(" minutes", "")
          : profile.preferred_duration === "Flexible"
            ? "flexible"
            : "",
        location: profile.location || "",
        onlineAvailable: profile.online_available || false,
        inPersonAvailable: profile.in_person_available || false,
        travelDistance: profile.max_travel_distance?.toString() || "",
        travelUnit: profile.travel_distance_unit || "miles",
        otherConsiderations: profile.travel_notes || "",
        // Load new travel preference fields
        travelMethod: profile.travel_method || [],
        carLimitType: profile.car_limit_type || "",
        maxCarDistance: profile.max_car_distance || "",
        maxCarTime: profile.max_car_time || "",
        maxTrainTime: profile.max_train_time || "",
        maxTrainChanges: profile.max_train_changes || "",
        availabilityType: profile.available_hours?.availability_type || "",
        minAdvanceNoticeDays:
          profile.available_hours?.min_advance_notice_days?.toString() || "",
        availabilityNotes: profile.available_hours?.availability_notes || "",
        pricingModel: profile.pricing_type || "",
        expensesNeeded: profile.audience_pricing?.expenses_needed || {
          travel: false,
          accommodation: false,
          equipment: false,
          meals: false,
        },
        honorariumAccepted:
          profile.audience_pricing?.honorarium_accepted ?? null,
        suggestedContribution:
          profile.audience_pricing?.suggested_contribution || "",
        minimumRate: profile.audience_pricing?.minimum_rate || "",
        sessionRate: profile.fixed_price?.toString() || "",
        sessionDuration: profile.audience_pricing?.session_duration || "",
        rateIncludes: profile.audience_pricing?.rate_includes || {
          travelIncluded: false,
          equipmentIncluded: false,
          preparationIncluded: false,
          qaIncluded: false,
        },
        negotiable: profile.audience_pricing?.negotiable ?? null,
        selectedDays: [],
      });

      setLoading(false);
    } catch (error) {
      console.error("Error loading profile:", error);
      alert("Error loading your profile. Please try again.");
      setLoading(false);
      window.location.href = "/explore";
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    try {
      setUploadingImage(true);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("You must be logged in to upload images");
        return;
      }

      // Create unique filename
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `profile-pictures/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from("profile-pictures")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("profile-pictures").getPublicUrl(filePath);

      // Update form data with image URL
      setFormData((prev) => ({
        ...prev,
        profilePictureUrl: publicUrl,
      }));

      // Set preview
      setImagePreview(publicUrl);

      setUploadingImage(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
      setUploadingImage(false);
    }
  };

  const options = ["Speaker", "Musician", "Other"];
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const handleContinue = () => {
    if (step === "service" && selectedTypes.length > 0) {
      setStep("details");
    } else if (step === "details") {
      setStep("service-info");
    } else if (step === "service-info") {
      // Use first selected type to determine which flow to follow
      // Travel/availability/pricing are the same for all types
      const firstType = selectedTypes[0].toLowerCase();
      setStep(`${firstType}-travel`);
    } else if (step.includes("-travel")) {
      const serviceType = step.split("-")[0];
      setStep(`${serviceType}-availability`);
    } else if (step.includes("-availability")) {
      const serviceType = step.split("-")[0];
      setStep(`${serviceType}-pricing`);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper function to toggle travel method selection
  const handleTravelMethodToggle = (method) => {
    setFormData((prev) => {
      const currentMethods = prev.travelMethod || [];
      const newMethods = currentMethods.includes(method)
        ? currentMethods.filter((m) => m !== method)
        : [...currentMethods, method];

      // Clear related fields when deselecting a method
      const updates = { travelMethod: newMethods };
      if (!newMethods.includes("car")) {
        updates.carLimitType = "";
        updates.maxCarDistance = "";
        updates.maxCarTime = "";
      }
      if (!newMethods.includes("train")) {
        updates.maxTrainTime = "";
        updates.maxTrainChanges = "";
      }

      return { ...prev, ...updates };
    });
  };

  // Helper function to toggle service type selection
  const handleServiceTypeToggle = (serviceType) => {
    setSelectedTypes((prev) => {
      if (prev.includes(serviceType)) {
        return prev.filter((type) => type !== serviceType);
      } else {
        return [...prev, serviceType];
      }
    });
  };

  const addTopic = () => {
    if (currentTopic.trim() && !formData.topics.includes(currentTopic.trim())) {
      setFormData((prev) => ({
        ...prev,
        topics: [...prev.topics, currentTopic.trim()],
      }));
      setCurrentTopic("");
    }
  };

  const removeTopic = (topicToRemove) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.filter((topic) => topic !== topicToRemove),
    }));
  };

  const addInstrument = () => {
    if (
      currentInstrument.trim() &&
      !formData.instruments.includes(currentInstrument.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        instruments: [...prev.instruments, currentInstrument.trim()],
      }));
      setCurrentInstrument("");
    }
  };

  const removeInstrument = (instrumentToRemove) => {
    setFormData((prev) => ({
      ...prev,
      instruments: prev.instruments.filter(
        (instrument) => instrument !== instrumentToRemove
      ),
    }));
  };

  const handleInstrumentKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addInstrument();
    }
  };

  const addTimeSlot = () => {
    if (
      currentTimeSlot.day &&
      currentTimeSlot.startTime &&
      currentTimeSlot.endTime
    ) {
      const timeSlotString = `${currentTimeSlot.day}: ${currentTimeSlot.startTime} - ${currentTimeSlot.endTime}`;
      if (!formData.availableHours.includes(timeSlotString)) {
        setFormData((prev) => ({
          ...prev,
          availableHours: [...prev.availableHours, timeSlotString],
        }));
        setCurrentTimeSlot({ day: "", startTime: "", endTime: "" });
      }
    }
  };

  const removeTimeSlot = (slotToRemove) => {
    setFormData((prev) => ({
      ...prev,
      availableHours: prev.availableHours.filter(
        (slot) => slot !== slotToRemove
      ),
    }));
  };

  const handleBack = () => {
    if (step === "details") {
      setStep("service");
    } else if (step === "service-info") {
      setStep("details");
    } else if (step.includes("-travel")) {
      setStep("service-info");
    } else if (step.includes("-availability")) {
      const serviceType = step.split("-")[0];
      setStep(`${serviceType}-travel`);
    } else if (step.includes("-pricing")) {
      const serviceType = step.split("-")[0];
      setStep(`${serviceType}-availability`);
    }
  };

  const handleSaveAndExit = async () => {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("You must be logged in to save");
        return;
      }

      // Save current progress
      const { error: profileError } = await supabase
        .from("user_profiles")
        .update({
          full_name: formData.name || null,
          church_name: formData.church || null,
          church_city: formData.churchCity || null,
          denomination: formData.denomination || null,
          qualifications: formData.qualifications || null,
          location: formData.location || null,
          bio2: formData.bio || null,
          bio1: formData.title || null,
          service_description: formData.description || null,
          age_groups: formData.ageGroups || null,
          service_types: selectedTypes.length > 0 ? selectedTypes : null,
          custom_service_type: formData.customServiceType || null,
          online_available: formData.onlineAvailable || false,
          in_person_available: formData.inPersonAvailable || false,
          max_travel_distance: formData.travelDistance
            ? parseInt(formData.travelDistance)
            : null,
          travel_distance_unit: formData.travelUnit || null,
          travel_notes: formData.otherConsiderations || null,
          preferred_duration: formData.typicalSessionLength || null,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id);

      if (profileError) throw profileError;

      // Save topics if any
      if (formData.topics.length > 0) {
        // Delete existing
        await supabase
          .from("speaker_expertise")
          .delete()
          .eq("user_id", user.id);

        // Insert new
        const expertiseRecords = formData.topics.map((topic) => ({
          user_id: user.id,
          expertise_area: topic,
          is_custom: true,
        }));

        await supabase.from("speaker_expertise").insert(expertiseRecords);
      }

      alert("Progress saved! You can continue your profile later.");
      window.location.href = "/explore";
    } catch (error) {
      console.error("Error saving progress:", error);
      alert("There was an error saving your progress. Please try again.");
    }
  };

  const addAudienceTier = () => {
    if (currentAudienceTier.range && currentAudienceTier.price) {
      const tierString = `${currentAudienceTier.range}: ${currentAudienceTier.price}`;
      if (!formData.audiencePricing.includes(tierString)) {
        setFormData((prev) => ({
          ...prev,
          audiencePricing: [...prev.audiencePricing, tierString],
        }));
        setCurrentAudienceTier({ range: "", price: "" });
      }
    }
  };

  const removeAudienceTier = (tierToRemove) => {
    setFormData((prev) => ({
      ...prev,
      audiencePricing: prev.audiencePricing.filter(
        (tier) => tier !== tierToRemove
      ),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTopic();
    }
  };

  const handleSubmit = async () => {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("You must be logged in to submit this form");
        return;
      }

      // Update user_profiles table - CHANGED TO UPSERT
      const { error: profileError } = await supabase
        .from("user_profiles")
        .upsert(
          {
            user_id: user.id, // ← Add this explicitly
            full_name: formData.name,
            church_name: formData.church,
            church_city: formData.churchCity || null,
            denomination: formData.denomination || null,
            qualifications: formData.qualifications || null,
            location: formData.location,
            bio2: formData.bio,
            profile_picture_url: formData.profilePictureUrl || null,
            bio1: formData.title,
            service_description: formData.description,
            age_groups: formData.ageGroups,
            service_types: selectedTypes,
            custom_service_type: formData.customServiceType || null,
            online_available: formData.onlineAvailable || false,
            in_person_available: formData.inPersonAvailable || false,
            skill_provider: true,
            skill_active: true,
            skill_active_time: new Date().toISOString(),
            max_travel_distance: parseInt(formData.travelDistance) || null,
            travel_distance_unit: formData.travelUnit || null,
            travel_method: formData.travelMethod,
            car_limit_type: formData.carLimitType || null,
            max_car_distance: formData.maxCarDistance || null,
            max_car_time: formData.maxCarTime || null,
            max_train_time: formData.maxTrainTime || null,
            max_train_changes: formData.maxTrainChanges || null,
            travel_notes: formData.otherConsiderations,
            preferred_duration:
              formData.typicalSessionLength === "flexible"
                ? "Flexible"
                : `${formData.typicalSessionLength} minutes`,
            available_hours: {
              availability_type: formData.availabilityType,
              min_advance_notice_days: parseInt(formData.minAdvanceNoticeDays),
              availability_notes: formData.availabilityNotes,
            },
            pricing_type: formData.pricingModel,
            fixed_price:
              formData.pricingModel === "set_rate"
                ? parseFloat(formData.sessionRate)
                : formData.suggestedContribution
                  ? parseFloat(formData.suggestedContribution)
                  : null,
            audience_pricing: {
              model: formData.pricingModel,
              ...(formData.pricingModel === "flexible" && {
                expenses_needed: formData.expensesNeeded,
                honorarium_accepted: formData.honorariumAccepted,
                suggested_contribution: formData.suggestedContribution,
                minimum_rate: formData.minimumRate,
              }),
              ...(formData.pricingModel === "set_rate" && {
                session_duration: formData.sessionDuration,
                rate_includes: formData.rateIncludes,
                negotiable: formData.negotiable,
              }),
            },
            updated_at: new Date().toISOString(),
            marketing_consent: formData.marketingConsent,
          },
          {
            onConflict: "user_id", // ← Specify which column to use for conflict resolution
          }
        );

      if (profileError) throw profileError;

      // Save topics if Speaker is selected, otherwise delete all topics
      if (selectedTypes.includes("Speaker")) {
        // Delete existing speaker expertise areas
        const { error: deleteError } = await supabase
          .from("speaker_expertise")
          .delete()
          .eq("user_id", user.id);

        if (deleteError) throw deleteError;

        // Insert new expertise areas
        const expertiseRecords = formData.topics.map((topic) => ({
          user_id: user.id,
          expertise_area: topic,
          is_custom: true,
        }));

        if (expertiseRecords.length > 0) {
          const { error: expertiseError } = await supabase
            .from("speaker_expertise")
            .insert(expertiseRecords);

          if (expertiseError) throw expertiseError;
        }
      } else {
        // User is no longer a Speaker, delete all their topics
        const { error: deleteError } = await supabase
          .from("speaker_expertise")
          .delete()
          .eq("user_id", user.id);

        if (deleteError) throw deleteError;
      }

      // Save instruments if Musician is selected, otherwise delete all instruments
      if (selectedTypes.includes("Musician")) {
        // Delete existing musician expertise
        const { error: deleteInstrumentsError } = await supabase
          .from("musician_expertise")
          .delete()
          .eq("user_id", user.id);

        // Only throw error if it's not a "table doesn't exist" error
        if (
          deleteInstrumentsError &&
          !deleteInstrumentsError.message.includes("does not exist")
        ) {
          throw deleteInstrumentsError;
        }

        // Insert new instruments
        const instrumentRecords = formData.instruments.map((instrument) => ({
          user_id: user.id,
          instrument: instrument,
          is_custom: true,
        }));

        if (instrumentRecords.length > 0) {
          const { error: instrumentsError } = await supabase
            .from("musician_expertise")
            .insert(instrumentRecords);

          // Only throw error if it's not a "table doesn't exist" error
          if (
            instrumentsError &&
            !instrumentsError.message.includes("does not exist")
          ) {
            throw instrumentsError;
          }
        }
      } else {
        // User is no longer a Musician, delete all their instruments
        const { error: deleteInstrumentsError } = await supabase
          .from("musician_expertise")
          .delete()
          .eq("user_id", user.id);

        // Only throw error if it's not a "table doesn't exist" error
        if (
          deleteInstrumentsError &&
          !deleteInstrumentsError.message.includes("does not exist")
        ) {
          throw deleteInstrumentsError;
        }
      }

      window.location.href = "/profile-complete";
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("There was an error saving your profile. Please try again.");
    }
  };
  const handleMusicianSubmit = async () => {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("You must be logged in to submit this form");
        return;
      }

      let audiencePricingData = null;
      if (formData.pricingType === "audience") {
        audiencePricingData = formData.audiencePricing.map((tier) => {
          const [range, priceStr] = tier.split(": £");
          return {
            range: range,
            price: parseFloat(priceStr),
          };
        });
      }

      const { error: profileError } = await supabase
        .from("user_profiles")
        .upsert(
          {
            user_id: user.id,
            full_name: formData.name,
            church_name: formData.church,
            church_city: formData.churchCity || null,
            denomination: formData.denomination || null,
            qualifications: formData.qualifications || null,
            location: formData.location,
            bio2: formData.bio,
            profile_picture_url: formData.profilePictureUrl || null,
            bio1: formData.title,
            service_description: formData.description,
            age_groups: formData.ageGroups,
            service_types: selectedTypes,
            custom_service_type: formData.customServiceType || null,
            online_available: formData.onlineAvailable || false,
            in_person_available: formData.inPersonAvailable || false,
            skill_provider: true,
            skill_active: true,
            skill_active_time: new Date().toISOString(),
            max_travel_distance: parseInt(formData.travelDistance) || null,
            travel_distance_unit: formData.travelUnit || null,
            travel_method: formData.travelMethod,
            car_limit_type: formData.carLimitType || null,
            max_car_distance: formData.maxCarDistance || null,
            max_car_time: formData.maxCarTime || null,
            max_train_time: formData.maxTrainTime || null,
            max_train_changes: formData.maxTrainChanges || null,
            travel_notes: formData.otherConsiderations,
            preferred_duration:
              formData.typicalSessionLength === "flexible"
                ? "Flexible"
                : `${formData.typicalSessionLength} minutes`,
            available_hours: {
              availability_type: formData.availabilityType,
              min_advance_notice_days: parseInt(formData.minAdvanceNoticeDays),
              availability_notes: formData.availabilityNotes,
            },
            pricing_type: formData.pricingModel,
            fixed_price:
              formData.pricingModel === "set_rate"
                ? parseFloat(formData.sessionRate)
                : formData.suggestedContribution
                  ? parseFloat(formData.suggestedContribution)
                  : null,
            audience_pricing: {
              model: formData.pricingModel,
              ...(formData.pricingModel === "flexible" && {
                expenses_needed: formData.expensesNeeded,
                honorarium_accepted: formData.honorariumAccepted,
                suggested_contribution: formData.suggestedContribution,
                minimum_rate: formData.minimumRate,
              }),
              ...(formData.pricingModel === "set_rate" && {
                session_duration: formData.sessionDuration,
                rate_includes: formData.rateIncludes,
                negotiable: formData.negotiable,
              }),
            },
            updated_at: new Date().toISOString(),
            marketing_consent: formData.marketingConsent,
          },
          {
            onConflict: "user_id", // ← Specify which column to use for conflict resolution
          }
        );

      if (profileError) throw profileError;

      const { error: deleteError } = await supabase
        .from("speaker_expertise")
        .delete()
        .eq("user_id", user.id);

      if (deleteError) throw deleteError;

      const expertiseRecords = formData.topics.map((topic) => ({
        user_id: user.id,
        expertise_area: topic,
        is_custom: true,
      }));

      if (expertiseRecords.length > 0) {
        const { error: expertiseError } = await supabase
          .from("speaker_expertise")
          .insert(expertiseRecords);

        if (expertiseError) throw expertiseError;
      }

      window.location.href = "/profile-complete";
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("There was an error saving your profile. Please try again.");
    }
  };

  const handleOtherSubmit = async () => {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert("You must be logged in to submit this form");
        return;
      }

      let audiencePricingData = null;
      if (formData.pricingType === "audience") {
        audiencePricingData = formData.audiencePricing.map((tier) => {
          const [range, priceStr] = tier.split(": £");
          return {
            range: range,
            price: parseFloat(priceStr),
          };
        });
      }

      const { error: profileError } = await supabase
        .from("user_profiles")
        .upsert(
          {
            user_id: user.id,
            full_name: formData.name,
            church_name: formData.church,
            church_city: formData.churchCity || null,
            denomination: formData.denomination || null,
            qualifications: formData.qualifications || null,
            location: formData.location,
            bio2: formData.bio,
            profile_picture_url: formData.profilePictureUrl || null,
            bio1: formData.title,
            service_description: formData.description,
            age_groups: formData.ageGroups,
            service_types: selectedTypes,
            custom_service_type: formData.customServiceType || null,
            online_available: formData.onlineAvailable || false,
            in_person_available: formData.inPersonAvailable || false,
            skill_provider: true,
            skill_active: true,
            skill_active_time: new Date().toISOString(),
            max_travel_distance: parseInt(formData.travelDistance) || null,
            travel_distance_unit: formData.travelUnit || null,
            travel_method: formData.travelMethod,
            car_limit_type: formData.carLimitType || null,
            max_car_distance: formData.maxCarDistance || null,
            max_car_time: formData.maxCarTime || null,
            max_train_time: formData.maxTrainTime || null,
            max_train_changes: formData.maxTrainChanges || null,
            travel_notes: formData.otherConsiderations,
            preferred_duration:
              formData.typicalSessionLength === "flexible"
                ? "Flexible"
                : `${formData.typicalSessionLength} minutes`,
            available_hours: {
              availability_type: formData.availabilityType,
              min_advance_notice_days: parseInt(formData.minAdvanceNoticeDays),
              availability_notes: formData.availabilityNotes,
            },
            pricing_type: formData.pricingModel,
            fixed_price:
              formData.pricingModel === "set_rate"
                ? parseFloat(formData.sessionRate)
                : formData.suggestedContribution
                  ? parseFloat(formData.suggestedContribution)
                  : null,
            audience_pricing: {
              model: formData.pricingModel,
              ...(formData.pricingModel === "flexible" && {
                expenses_needed: formData.expensesNeeded,
                honorarium_accepted: formData.honorariumAccepted,
                suggested_contribution: formData.suggestedContribution,
                minimum_rate: formData.minimumRate,
              }),
              ...(formData.pricingModel === "set_rate" && {
                session_duration: formData.sessionDuration,
                rate_includes: formData.rateIncludes,
                negotiable: formData.negotiable,
              }),
            },
            updated_at: new Date().toISOString(),
            marketing_consent: formData.marketingConsent,
          },
          {
            onConflict: "user_id", // ← Add this
          }
        );

      if (profileError) throw profileError;

      // Save topics if Speaker is selected, otherwise delete all topics
      if (selectedTypes.includes("Speaker")) {
        // Delete existing speaker expertise areas
        const { error: deleteError } = await supabase
          .from("speaker_expertise")
          .delete()
          .eq("user_id", user.id);

        if (deleteError) throw deleteError;

        // Insert new expertise areas
        const expertiseRecords = formData.topics.map((topic) => ({
          user_id: user.id,
          expertise_area: topic,
          is_custom: true,
        }));

        if (expertiseRecords.length > 0) {
          const { error: expertiseError } = await supabase
            .from("speaker_expertise")
            .insert(expertiseRecords);

          if (expertiseError) throw expertiseError;
        }
      } else {
        // User is no longer a Speaker, delete all their topics
        const { error: deleteError } = await supabase
          .from("speaker_expertise")
          .delete()
          .eq("user_id", user.id);

        if (deleteError) throw deleteError;
      }

      // Save instruments if Musician is selected, otherwise delete all instruments
      if (selectedTypes.includes("Musician")) {
        // Delete existing musician expertise
        const { error: deleteInstrumentsError } = await supabase
          .from("musician_expertise")
          .delete()
          .eq("user_id", user.id);

        // Only throw error if it's not a "table doesn't exist" error
        if (
          deleteInstrumentsError &&
          !deleteInstrumentsError.message.includes("does not exist")
        ) {
          throw deleteInstrumentsError;
        }

        // Insert new instruments
        const instrumentRecords = formData.instruments.map((instrument) => ({
          user_id: user.id,
          instrument: instrument,
          is_custom: true,
        }));

        if (instrumentRecords.length > 0) {
          const { error: instrumentsError } = await supabase
            .from("musician_expertise")
            .insert(instrumentRecords);

          // Only throw error if it's not a "table doesn't exist" error
          if (
            instrumentsError &&
            !instrumentsError.message.includes("does not exist")
          ) {
            throw instrumentsError;
          }
        }
      } else {
        // User is no longer a Musician, delete all their instruments
        const { error: deleteInstrumentsError } = await supabase
          .from("musician_expertise")
          .delete()
          .eq("user_id", user.id);

        // Only throw error if it's not a "table doesn't exist" error
        if (
          deleteInstrumentsError &&
          !deleteInstrumentsError.message.includes("does not exist")
        ) {
          throw deleteInstrumentsError;
        }
      }

      // alert("Your profile has been saved successfully!");
      window.location.href = "/profile-complete";
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("There was an error saving your profile. Please try again.");
    }
  };

  const isStep1Valid = formData.name && formData.church && formData.bio;
  const isStep2Valid = formData.title && formData.topics.length > 0;

  // Step 3 validation: Check travel method selections and conditional fields
  const isStep3Valid = (() => {
    // Location is always required
    if (!formData.location) return false;

    // Must have at least one travel method selected
    if (!formData.travelMethod || formData.travelMethod.length === 0)
      return false;

    // If car is selected, validate car fields
    if (formData.travelMethod.includes("car")) {
      // carLimitType is required
      if (!formData.carLimitType) return false;

      // If distance limit, maxCarDistance is required
      if (formData.carLimitType === "distance" && !formData.maxCarDistance)
        return false;

      // If time limit, maxCarTime is required
      if (formData.carLimitType === "time" && !formData.maxCarTime)
        return false;
    }

    // If train is selected, validate train fields
    if (formData.travelMethod.includes("train")) {
      if (!formData.maxTrainTime || !formData.maxTrainChanges) return false;
    }

    return true;
  })();

  const isStep4Valid =
    formData.availabilityType && formData.minAdvanceNoticeDays;
  const isStep5Valid =
    formData.pricingModel === "free" ||
    (formData.pricingModel === "flexible" &&
      formData.honorariumAccepted !== null) ||
    (formData.pricingModel === "set_rate" &&
      formData.sessionRate &&
      formData.sessionDuration &&
      formData.negotiable !== null);

  // ADD THIS - Show loading state in edit mode
  if (loading && editMode) {
    return (
      <div style={styles.container}>
        <p style={{ fontSize: "15px", color: "#525252" }}>
          Loading your profile...
        </p>
      </div>
    );
  }

  // Step 5: Pricing (Speaker)
  if (step === "speaker-pricing") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <FormNavigation
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
          />

          <StepIndicator currentStep={5} totalSteps={5} />

          <FormHeader title="How would you like to offer your services?" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "32px",
            }}
          >
            <button
              onClick={() => handleInputChange("pricingModel", "free")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color: formData.pricingModel === "free" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "free"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "free") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "free") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Free Ministry
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I want to serve freely, including covering my own costs
              </div>
            </button>

            <button
              onClick={() => handleInputChange("pricingModel", "flexible")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color:
                  formData.pricingModel === "flexible" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "flexible"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "flexible") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "flexible") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Flexible / Expense-Based
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I'm open to discussion based on church budget and needs
              </div>
            </button>

            <button
              onClick={() => handleInputChange("pricingModel", "set_rate")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color:
                  formData.pricingModel === "set_rate" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "set_rate"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "set_rate") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "set_rate") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Set Rate
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I charge a standard rate for my services
              </div>
            </button>
          </div>

          {/* Flexible Pricing Options */}
          {formData.pricingModel === "flexible" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "32px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  What expenses would you need covered?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {[
                    { id: "travel", label: "Travel costs" },
                    {
                      id: "accommodation",
                      label: "Accommodation (if required)",
                    },
                    { id: "equipment", label: "Equipment/materials" },
                    { id: "meals", label: "Meal allowance" },
                  ].map((expense) => (
                    <label
                      key={expense.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        color: "#525252",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.expensesNeeded?.[expense.id] || false}
                        onChange={(e) => {
                          const newExpenses = {
                            ...formData.expensesNeeded,
                            [expense.id]: e.target.checked,
                          };
                          handleInputChange("expensesNeeded", newExpenses);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span>{expense.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  Would you accept an honorarium if offered?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() =>
                      handleInputChange("honorariumAccepted", true)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.honorariumAccepted === true
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.honorariumAccepted === true
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.honorariumAccepted !== true) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.honorariumAccepted !== true) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    Yes, open to honorarium
                  </button>
                  <button
                    onClick={() =>
                      handleInputChange("honorariumAccepted", false)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.honorariumAccepted === false
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.honorariumAccepted === false
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.honorariumAccepted !== false) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.honorariumAccepted !== false) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    No, expenses only
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Suggested contribution{" "}
                  <span style={{ color: "#a3a3a3", fontWeight: "400" }}>
                    (optional)
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#737373",
                    margin: "0 0 8px 0",
                  }}
                >
                  Churches often ask for guidance on appropriate amounts
                </p>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.suggestedContribution}
                    onChange={(e) =>
                      handleInputChange("suggestedContribution", e.target.value)
                    }
                    // placeholder="e.g., 100, 200"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div>

              {/* <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  If budget allows, minimum acceptable{" "}
                  <span style={{ color: "#a3a3a3", fontWeight: "400" }}>
                    (optional)
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#737373",
                    margin: "0 0 8px 0",
                  }}
                >
                  Sets a floor for what you can accept if charging
                </p>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.minimumRate}
                    onChange={(e) =>
                      handleInputChange("minimumRate", e.target.value)
                    }
                    placeholder="e.g., 75, 100"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div> */}
            </div>
          )}

          {/* Set Rate Options */}
          {formData.pricingModel === "set_rate" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "32px",
              }}
            >
              {/* <div
                style={{
                  textAlign: "center",
                  marginBottom: "12px",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      "Pricing tips:\n\n• Research rates in your area\n• Factor in preparation time\n• Include travel costs within your radius\n• Account for platform fees\n• Consider your experience level\n\nTypical rates:\n• Entry level: £50-150/session\n• Experienced: £150-400/session\n• Expert: £400+/session"
                    )
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips on pricing
                </button>
              </div> */}

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Typical rate per session
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.sessionRate}
                    onChange={(e) =>
                      handleInputChange("sessionRate", e.target.value)
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div>

              {/* <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Typical session duration
                </label>
                <select
                  value={formData.sessionDuration}
                  onChange={(e) =>
                    handleInputChange("sessionDuration", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    color: formData.sessionDuration ? "#1a1a1a" : "#a3a3a3",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    outline: "none",
                    cursor: "pointer",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#a3a3a3")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e5e5e5")
                  }
                >
                  <option value="">Select duration</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">2 hours</option>
                  <option value="240">Half day (4 hours)</option>
                  <option value="480">Full day (8 hours)</option>
                </select>
              </div> */}

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  This rate includes (select all that apply)
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {[
                    {
                      id: "travelIncluded",
                      label: "Travel within service radius",
                    },
                    { id: "equipmentIncluded", label: "Basic equipment/setup" },
                    { id: "preparationIncluded", label: "Preparation time" },
                    { id: "qaIncluded", label: "Q&A session" },
                  ].map((item) => (
                    <label
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        color: "#525252",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.rateIncludes?.[item.id] || false}
                        onChange={(e) => {
                          const newIncludes = {
                            ...formData.rateIncludes,
                            [item.id]: e.target.checked,
                          };
                          handleInputChange("rateIncludes", newIncludes);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  Willing to negotiate pricing?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() => handleInputChange("negotiable", true)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.negotiable === true ? "#000" : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.negotiable === true
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.negotiable !== true) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.negotiable !== true) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    Yes, open to discussion
                  </button>
                  <button
                    onClick={() => handleInputChange("negotiable", false)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.negotiable === false ? "#000" : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.negotiable === false
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.negotiable !== false) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.negotiable !== false) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    No, rate is fixed
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Email Marketing Consent */}
          <div
            style={{
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: "1px solid #e5e5e5",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={formData.marketingConsent}
                onChange={(e) =>
                  handleInputChange("marketingConsent", e.target.checked)
                }
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "15px",
                  color: "#1a1a1a",
                  lineHeight: "1.5",
                }}
              >
                Email me about new features and platform updates
              </span>
            </label>
            <p
              style={{
                fontSize: "13px",
                color: "#737373",
                marginTop: "8px",
                marginLeft: "30px",
                lineHeight: "1.5",
              }}
            >
              You'll always receive important notifications about bookings and
              messages regardless of this setting
            </p>
          </div>

          <button
            disabled={!isStep5Valid}
            onClick={handleSubmit}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep5Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep5Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep5Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep5Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep5Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Availability (Speaker)
  if (step === "speaker-availability") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={4} totalSteps={5} />

          <FormHeader title="When can churches book you?" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                }}
              >
                General availability
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                {[
                  { value: "weekdays", label: "Weekdays" },
                  { value: "weekends", label: "Weekends" },
                  { value: "evenings", label: "Evenings" },
                  { value: "weekday_evenings", label: "Weekday evenings" },
                  { value: "sundays", label: "Sunday services" },
                  { value: "flexible", label: "Very flexible" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      handleInputChange("availabilityType", option.value)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.availabilityType === option.value
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.availabilityType === option.value
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.availabilityType !== option.value) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.availabilityType !== option.value) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <SelectDropdown
                label="Minimum advance notice"
                value={formData.minAdvanceNoticeDays}
                onChange={(e) =>
                  handleInputChange("minAdvanceNoticeDays", e.target.value)
                }
                helperText="How much notice do you need before a booking?"
                placeholder="Select notice period"
                options={[
                  { value: "0", label: "Same day (if available)" },
                  { value: "1", label: "24 hours" },
                  { value: "2", label: "48 hours" },
                  { value: "3", label: "3 days" },
                  { value: "7", label: "1 week" },
                  { value: "14", label: "2 weeks" },
                  { value: "30", label: "1 month" },
                ]}
              />
            </div>

            <div>
              <TextArea
                label="Additional availability notes"
                value={formData.availabilityNotes}
                onChange={(e) =>
                  handleInputChange("availabilityNotes", e.target.value)
                }
                rows={4}
                placeholder="e.g., 'Available most weekday evenings after 6 PM. Unavailable July-August (summer break). Prefer morning sessions but flexible for special events.'"
                helperText="Share any specific availability details, blackout periods, or preferences"
                optional={true}
              />
            </div>
          </div>

          <button
            disabled={!isStep4Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep4Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep4Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep4Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep4Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep4Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }
  // Step 3: Travel
  if (step === "speaker-travel") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={3} totalSteps={5} />

          <FormHeader title="Travel" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Your city or town"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              placeholder="Enter your city or town"
            />

            {/* Travel Method Selection */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                }}
              >
                How do you typically travel to engagements?
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                {[
                  { value: "car", label: "Car" },
                  { value: "train", label: "Train" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTravelMethodToggle(option.value)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.travelMethod.includes(option.value)
                        ? "#000"
                        : "#525252",
                      backgroundColor: "#fff",
                      border: formData.travelMethod.includes(option.value)
                        ? "2px solid #000"
                        : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "left",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Car Travel Fields - Conditional */}
            {formData.travelMethod.includes("car") && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#525252",
                      marginBottom: "12px",
                    }}
                  >
                    I prefer to set my car travel limit by:
                  </label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    {[
                      { value: "distance", label: "Distance" },
                      { value: "time", label: "Time" },
                    ].map((option) => (
                      <label
                        key={option.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "15px",
                          color: "#525252",
                        }}
                      >
                        <input
                          type="radio"
                          name="carLimitType"
                          value={option.value}
                          checked={formData.carLimitType === option.value}
                          onChange={(e) =>
                            handleInputChange("carLimitType", e.target.value)
                          }
                          style={{ cursor: "pointer" }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {formData.carLimitType === "distance" && (
                  <SelectDropdown
                    label="Maximum distance willing to travel by car"
                    value={formData.maxCarDistance}
                    onChange={(e) =>
                      handleInputChange("maxCarDistance", e.target.value)
                    }
                    placeholder="Select distance"
                    options={[
                      { value: "10 miles", label: "10 miles" },
                      { value: "25 miles", label: "25 miles" },
                      { value: "50 miles", label: "50 miles" },
                      { value: "75 miles", label: "75 miles" },
                      { value: "100 miles", label: "100 miles" },
                      { value: "150+ miles", label: "150+ miles" },
                      { value: "Flexible", label: "Flexible" },
                    ]}
                  />
                )}

                {formData.carLimitType === "time" && (
                  <SelectDropdown
                    label="Maximum driving time"
                    value={formData.maxCarTime}
                    onChange={(e) =>
                      handleInputChange("maxCarTime", e.target.value)
                    }
                    placeholder="Select time"
                    options={[
                      { value: "30 minutes", label: "30 minutes" },
                      { value: "1 hour", label: "1 hour" },
                      { value: "1.5 hours", label: "1.5 hours" },
                      { value: "2 hours", label: "2 hours" },
                      { value: "2.5 hours", label: "2.5 hours" },
                      { value: "3+ hours", label: "3+ hours" },
                      { value: "Flexible", label: "Flexible" },
                    ]}
                  />
                )}
              </>
            )}

            {/* Train Travel Fields - Conditional */}
            {formData.travelMethod.includes("train") && (
              <>
                <SelectDropdown
                  label="Maximum train journey time"
                  value={formData.maxTrainTime}
                  onChange={(e) =>
                    handleInputChange("maxTrainTime", e.target.value)
                  }
                  placeholder="Select time"
                  options={[
                    { value: "1 hour", label: "1 hour" },
                    { value: "1.5 hours", label: "1.5 hours" },
                    { value: "2 hours", label: "2 hours" },
                    { value: "2.5 hours", label: "2.5 hours" },
                    { value: "3+ hours", label: "3+ hours" },
                    { value: "Flexible", label: "Flexible" },
                  ]}
                />

                <SelectDropdown
                  label="Maximum number of train changes"
                  value={formData.maxTrainChanges}
                  onChange={(e) =>
                    handleInputChange("maxTrainChanges", e.target.value)
                  }
                  placeholder="Select changes"
                  options={[
                    { value: "Direct only", label: "Direct only" },
                    { value: "1 change", label: "1 change" },
                    { value: "2 changes", label: "2 changes" },
                    { value: "3+ changes", label: "3+ changes" },
                    { value: "No preference", label: "No preference" },
                  ]}
                />
              </>
            )}

            <TextArea
              label="Any other considerations?"
              optional={true}
              value={formData.otherConsiderations}
              onChange={(e) =>
                handleInputChange("otherConsiderations", e.target.value)
              }
              rows={4}
            />
          </div>

          <button
            disabled={!isStep3Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep3Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep3Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep3Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep3Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep3Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Service Details
  if (step === "speaker-service") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={2} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            Give your service a title
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                  }}
                >
                  Service Title
                </label>
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      'Tips: Keep it clear and descriptive. Examples:\n• "Youth Ministry Leadership"\n• "Marriage & Family Counseling"\n• "Biblical Teaching for Small Groups"'
                    )
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips
                </button>
              </div>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Topics for your talks (expertise areas)
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={currentTopic}
                  onChange={(e) => setCurrentTopic(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type and press Add"
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    fontSize: "15px",
                    color: "#1a1a1a",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#a3a3a3")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e5e5e5")
                  }
                />
                <button
                  onClick={addTopic}
                  disabled={!currentTopic.trim()}
                  style={{
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: currentTopic.trim() ? "#000" : "#a3a3a3",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    cursor: currentTopic.trim() ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (currentTopic.trim())
                      e.currentTarget.style.borderColor = "#a3a3a3";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                  }}
                >
                  Add
                </button>
              </div>
              {formData.topics.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {formData.topics.map((topic, index) => (
                    <div
                      key={index}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        color: "#1a1a1a",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "6px",
                      }}
                    >
                      {topic}
                      <button
                        onClick={() => removeTopic(topic)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#737373",
                          cursor: "pointer",
                          padding: "0",
                          fontSize: "18px",
                          lineHeight: "1",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#1a1a1a")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "#737373")
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <TextArea
              label="Describe your service"
              optional={true}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={4}
            />

            <AgeGroupSelector
              label="Who is your ministry for?"
              helperText="Select all age groups that apply"
              value={formData.ageGroups}
              onChange={(newValue) => handleInputChange("ageGroups", newValue)}
            />

            {/* <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Typical session length
              </label>
              <select
                value={formData.typicalSessionLength}
                onChange={(e) =>
                  handleInputChange("typicalSessionLength", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: formData.typicalSessionLength ? "#1a1a1a" : "#a3a3a3",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              >
                <option value="">Select duration</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">90 minutes</option>
                <option value="120">2 hours</option>
                <option value="180">3 hours</option>
                <option value="240">Half day (4 hours)</option>
                <option value="480">Full day (8 hours)</option>
                <option value="flexible">Flexible / Varies</option>
              </select>
            </div> */}
          </div>

          <button
            disabled={!isStep2Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep2Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep2Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep2Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep2Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep2Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Service Details (Musician)
  if (step === "musician-service") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={2} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            Give your service a title
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                  }}
                >
                  Service Title
                </label>
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      'Tips: Keep it clear and descriptive. Examples:\n• "Worship Band Leader"\n• "Classical Piano Performance"\n• "Contemporary Worship Guitar"'
                    )
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips
                </button>
              </div>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                What instruments do you play? (expertise areas)
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={currentTopic}
                  onChange={(e) => setCurrentTopic(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type and press Add"
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    fontSize: "15px",
                    color: "#1a1a1a",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#a3a3a3")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e5e5e5")
                  }
                />
                <button
                  onClick={addTopic}
                  disabled={!currentTopic.trim()}
                  style={{
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: currentTopic.trim() ? "#000" : "#a3a3a3",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    cursor: currentTopic.trim() ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (currentTopic.trim())
                      e.currentTarget.style.borderColor = "#a3a3a3";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                  }}
                >
                  Add
                </button>
              </div>
              {formData.topics.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {formData.topics.map((topic, index) => (
                    <div
                      key={index}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        color: "#1a1a1a",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "6px",
                      }}
                    >
                      {topic}
                      <button
                        onClick={() => removeTopic(topic)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#737373",
                          cursor: "pointer",
                          padding: "0",
                          fontSize: "18px",
                          lineHeight: "1",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#1a1a1a")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "#737373")
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <TextArea
              label="Describe your service"
              optional={true}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={4}
            />

            <AgeGroupSelector
              label="Who is your ministry for?"
              helperText="Select all age groups that apply"
              value={formData.ageGroups}
              onChange={(newValue) => handleInputChange("ageGroups", newValue)}
            />

            {/* <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Typical session length
              </label>
              <select
                value={formData.typicalSessionLength}
                onChange={(e) =>
                  handleInputChange("typicalSessionLength", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: formData.typicalSessionLength ? "#1a1a1a" : "#a3a3a3",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              >
                <option value="">Select duration</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">90 minutes</option>
                <option value="120">2 hours</option>
                <option value="180">3 hours</option>
                <option value="240">Half day (4 hours)</option>
                <option value="480">Full day (8 hours)</option>
                <option value="flexible">Flexible / Varies</option>
              </select>
            </div> */}
          </div>

          <button
            disabled={!isStep2Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep2Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep2Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep2Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep2Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep2Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Service Details (Other)
  if (step === "other-service") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={2} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            Give your service a title
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                  }}
                >
                  Service Title
                </label>
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      'Tips: Keep it clear and descriptive. Examples:\n• "Event Photography"\n• "Sound Engineering"\n• "Graphic Design Services"'
                    )
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips
                </button>
              </div>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                What are your expertise areas?
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={currentTopic}
                  onChange={(e) => setCurrentTopic(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type and press Add"
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    fontSize: "15px",
                    color: "#1a1a1a",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#a3a3a3")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e5e5e5")
                  }
                />
                <button
                  onClick={addTopic}
                  disabled={!currentTopic.trim()}
                  style={{
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: currentTopic.trim() ? "#000" : "#a3a3a3",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    cursor: currentTopic.trim() ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    if (currentTopic.trim())
                      e.currentTarget.style.borderColor = "#a3a3a3";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                  }}
                >
                  Add
                </button>
              </div>
              {formData.topics.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {formData.topics.map((topic, index) => (
                    <div
                      key={index}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        fontSize: "14px",
                        color: "#1a1a1a",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "6px",
                      }}
                    >
                      {topic}
                      <button
                        onClick={() => removeTopic(topic)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#737373",
                          cursor: "pointer",
                          padding: "0",
                          fontSize: "18px",
                          lineHeight: "1",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#1a1a1a")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "#737373")
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <TextArea
              label="Describe your service"
              optional={true}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={4}
            />

            <AgeGroupSelector
              label="Who is your ministry for?"
              helperText="Select all age groups that apply"
              value={formData.ageGroups}
              onChange={(newValue) => handleInputChange("ageGroups", newValue)}
            />

            {/* <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Typical session length
              </label>
              <select
                value={formData.typicalSessionLength}
                onChange={(e) =>
                  handleInputChange("typicalSessionLength", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: formData.typicalSessionLength ? "#1a1a1a" : "#a3a3a3",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              >
                <option value="">Select duration</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">90 minutes</option>
                <option value="120">2 hours</option>
                <option value="180">3 hours</option>
                <option value="240">Half day (4 hours)</option>
                <option value="480">Full day (8 hours)</option>
                <option value="flexible">Flexible / Varies</option>
              </select>
            </div> */}
          </div>

          <button
            disabled={!isStep2Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep2Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep2Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep2Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep2Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep2Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Personal Details (Musician)
  if (step === "musician-details") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={1} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            {editMode ? "Update your details" : "Tell us a bit more about you"}
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Profile Picture
              </label>
              <p
                style={{
                  fontSize: "13px",
                  color: "#737373",
                  margin: "0 0 8px 0",
                }}
              >
                Upload a professional photo (max 5MB)
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                {(imagePreview || formData.profilePictureUrl) && (
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "2px solid #e5e5e5",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={imagePreview || formData.profilePictureUrl}
                      alt="Profile preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
                <label
                  style={{
                    display: "inline-block",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: uploadingImage ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: uploadingImage ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#a3a3a3";
                      e.currentTarget.style.backgroundColor = "#fafafa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  {uploadingImage ? "Uploading..." : "Choose Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <TextInput
              label="Your Church"
              value={formData.church}
              onChange={(e) => handleInputChange("church", e.target.value)}
            />

            {/* <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div> */}

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Tell us about you
              </label>
              <p
                style={{
                  fontSize: "13px",
                  color: "#737373",
                  margin: "0 0 8px 0",
                }}
              >
                Share your qualifications and experience
              </p>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
                rows={4}
                placeholder="Tell us about your background, qualifications, and experience..."
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div>
          </div>

          <button
            disabled={!isStep1Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep1Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep1Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep1Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Travel (Musician)
  if (step === "musician-travel") {
    // Copy the entire speaker-travel block and just change step === 'speaker-travel' to step === 'musician-travel'
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={3} totalSteps={5} />

          <FormHeader title="Travel" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Your city or town"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              placeholder="Enter your city or town"
            />

            {/* Travel Method Selection */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                }}
              >
                How do you typically travel to engagements?
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                {[
                  { value: "car", label: "Car" },
                  { value: "train", label: "Train" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTravelMethodToggle(option.value)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.travelMethod.includes(option.value)
                        ? "#000"
                        : "#525252",
                      backgroundColor: "#fff",
                      border: formData.travelMethod.includes(option.value)
                        ? "2px solid #000"
                        : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "left",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Car Travel Fields - Conditional */}
            {formData.travelMethod.includes("car") && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#525252",
                      marginBottom: "12px",
                    }}
                  >
                    I prefer to set my car travel limit by:
                  </label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    {[
                      { value: "distance", label: "Distance" },
                      { value: "time", label: "Time" },
                    ].map((option) => (
                      <label
                        key={option.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "15px",
                          color: "#525252",
                        }}
                      >
                        <input
                          type="radio"
                          name="carLimitType"
                          value={option.value}
                          checked={formData.carLimitType === option.value}
                          onChange={(e) =>
                            handleInputChange("carLimitType", e.target.value)
                          }
                          style={{ cursor: "pointer" }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {formData.carLimitType === "distance" && (
                  <SelectDropdown
                    label="Maximum distance willing to travel by car"
                    value={formData.maxCarDistance}
                    onChange={(e) =>
                      handleInputChange("maxCarDistance", e.target.value)
                    }
                    placeholder="Select distance"
                    options={[
                      { value: "10 miles", label: "10 miles" },
                      { value: "25 miles", label: "25 miles" },
                      { value: "50 miles", label: "50 miles" },
                      { value: "75 miles", label: "75 miles" },
                      { value: "100 miles", label: "100 miles" },
                      { value: "150+ miles", label: "150+ miles" },
                      { value: "Flexible", label: "Flexible" },
                    ]}
                  />
                )}

                {formData.carLimitType === "time" && (
                  <SelectDropdown
                    label="Maximum driving time"
                    value={formData.maxCarTime}
                    onChange={(e) =>
                      handleInputChange("maxCarTime", e.target.value)
                    }
                    placeholder="Select time"
                    options={[
                      { value: "30 minutes", label: "30 minutes" },
                      { value: "1 hour", label: "1 hour" },
                      { value: "1.5 hours", label: "1.5 hours" },
                      { value: "2 hours", label: "2 hours" },
                      { value: "2.5 hours", label: "2.5 hours" },
                      { value: "3+ hours", label: "3+ hours" },
                      { value: "Flexible", label: "Flexible" },
                    ]}
                  />
                )}
              </>
            )}

            {/* Train Travel Fields - Conditional */}
            {formData.travelMethod.includes("train") && (
              <>
                <SelectDropdown
                  label="Maximum train journey time"
                  value={formData.maxTrainTime}
                  onChange={(e) =>
                    handleInputChange("maxTrainTime", e.target.value)
                  }
                  placeholder="Select time"
                  options={[
                    { value: "1 hour", label: "1 hour" },
                    { value: "1.5 hours", label: "1.5 hours" },
                    { value: "2 hours", label: "2 hours" },
                    { value: "2.5 hours", label: "2.5 hours" },
                    { value: "3+ hours", label: "3+ hours" },
                    { value: "Flexible", label: "Flexible" },
                  ]}
                />

                <SelectDropdown
                  label="Maximum number of train changes"
                  value={formData.maxTrainChanges}
                  onChange={(e) =>
                    handleInputChange("maxTrainChanges", e.target.value)
                  }
                  placeholder="Select changes"
                  options={[
                    { value: "Direct only", label: "Direct only" },
                    { value: "1 change", label: "1 change" },
                    { value: "2 changes", label: "2 changes" },
                    { value: "3+ changes", label: "3+ changes" },
                    { value: "No preference", label: "No preference" },
                  ]}
                />
              </>
            )}

            <TextArea
              label="Any other considerations?"
              optional={true}
              value={formData.otherConsiderations}
              onChange={(e) =>
                handleInputChange("otherConsiderations", e.target.value)
              }
              rows={4}
            />
          </div>

          <button
            disabled={!isStep3Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep3Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep3Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep3Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep3Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep3Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Availability (Musician)
  if (step === "musician-availability") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={4} totalSteps={5} />

          <FormHeader title="When can churches book you?" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                }}
              >
                General availability
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                {[
                  { value: "weekdays", label: "Weekdays" },
                  { value: "weekends", label: "Weekends" },
                  { value: "evenings", label: "Evenings" },
                  { value: "weekday_evenings", label: "Weekday evenings" },
                  { value: "sundays", label: "Sunday services" },
                  { value: "flexible", label: "Very flexible" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      handleInputChange("availabilityType", option.value)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.availabilityType === option.value
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.availabilityType === option.value
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.availabilityType !== option.value) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.availabilityType !== option.value) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <SelectDropdown
                label="Minimum advance notice"
                value={formData.minAdvanceNoticeDays}
                onChange={(e) =>
                  handleInputChange("minAdvanceNoticeDays", e.target.value)
                }
                helperText="How much notice do you need before a booking?"
                placeholder="Select notice period"
                options={[
                  { value: "0", label: "Same day (if available)" },
                  { value: "1", label: "24 hours" },
                  { value: "2", label: "48 hours" },
                  { value: "3", label: "3 days" },
                  { value: "7", label: "1 week" },
                  { value: "14", label: "2 weeks" },
                  { value: "30", label: "1 month" },
                ]}
              />
            </div>

            <div>
              <TextArea
                label="Additional availability notes"
                value={formData.availabilityNotes}
                onChange={(e) =>
                  handleInputChange("availabilityNotes", e.target.value)
                }
                rows={4}
                placeholder="e.g., 'Available most weekday evenings after 6 PM. Unavailable July-August (summer break). Prefer morning sessions but flexible for special events.'"
                helperText="Share any specific availability details, blackout periods, or preferences"
                optional={true}
              />
            </div>
          </div>

          <button
            disabled={!isStep4Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep4Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep4Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep4Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep4Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep4Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }
  // Step 5: Pricing (Musician)
  if (step === "musician-pricing") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <FormNavigation
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
          />

          <StepIndicator currentStep={5} totalSteps={5} />

          <FormHeader title="How would you like to offer your services?" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "32px",
            }}
          >
            <button
              onClick={() => handleInputChange("pricingModel", "free")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color: formData.pricingModel === "free" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "free"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "free") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "free") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Free Ministry
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I want to serve freely, including covering my own costs
              </div>
            </button>

            <button
              onClick={() => handleInputChange("pricingModel", "flexible")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color:
                  formData.pricingModel === "flexible" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "flexible"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "flexible") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "flexible") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Flexible / Expense-Based
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I'm open to discussion based on church budget and needs
              </div>
            </button>

            <button
              onClick={() => handleInputChange("pricingModel", "set_rate")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color:
                  formData.pricingModel === "set_rate" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "set_rate"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "set_rate") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "set_rate") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Set Rate
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I charge a standard rate for my services
              </div>
            </button>
          </div>

          {/* Flexible Pricing Options */}
          {formData.pricingModel === "flexible" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "32px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  What expenses would you need covered?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {[
                    { id: "travel", label: "Travel costs" },
                    {
                      id: "accommodation",
                      label: "Accommodation (if required)",
                    },
                    { id: "equipment", label: "Equipment/materials" },
                    { id: "meals", label: "Meal allowance" },
                  ].map((expense) => (
                    <label
                      key={expense.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        color: "#525252",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.expensesNeeded?.[expense.id] || false}
                        onChange={(e) => {
                          const newExpenses = {
                            ...formData.expensesNeeded,
                            [expense.id]: e.target.checked,
                          };
                          handleInputChange("expensesNeeded", newExpenses);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span>{expense.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  Would you accept an honorarium if offered?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() =>
                      handleInputChange("honorariumAccepted", true)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.honorariumAccepted === true
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.honorariumAccepted === true
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.honorariumAccepted !== true) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.honorariumAccepted !== true) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    Yes, open to honorarium
                  </button>
                  <button
                    onClick={() =>
                      handleInputChange("honorariumAccepted", false)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.honorariumAccepted === false
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.honorariumAccepted === false
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.honorariumAccepted !== false) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.honorariumAccepted !== false) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    No, expenses only
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Suggested contribution{" "}
                  <span style={{ color: "#a3a3a3", fontWeight: "400" }}>
                    (optional)
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#737373",
                    margin: "0 0 8px 0",
                  }}
                >
                  Churches often ask for guidance on appropriate amounts
                </p>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.suggestedContribution}
                    onChange={(e) =>
                      handleInputChange("suggestedContribution", e.target.value)
                    }
                    // placeholder="e.g., 100, 200"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div>

              {/* <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  If budget allows, minimum acceptable{" "}
                  <span style={{ color: "#a3a3a3", fontWeight: "400" }}>
                    (optional)
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#737373",
                    margin: "0 0 8px 0",
                  }}
                >
                  Sets a floor for what you can accept if charging
                </p>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.minimumRate}
                    onChange={(e) =>
                      handleInputChange("minimumRate", e.target.value)
                    }
                    placeholder="e.g., 75, 100"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div> */}
            </div>
          )}

          {/* Set Rate Options */}
          {formData.pricingModel === "set_rate" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "32px",
              }}
            >
              {/* <div
                style={{
                  textAlign: "center",
                  marginBottom: "12px",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      "Pricing tips:\n\n• Research rates in your area\n• Factor in preparation time\n• Include travel costs within your radius\n• Account for platform fees\n• Consider your experience level\n\nTypical rates:\n• Entry level: £50-150/session\n• Experienced: £150-400/session\n• Expert: £400+/session"
                    )
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips on pricing
                </button>
              </div> */}

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Typical rate per session
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.sessionRate}
                    onChange={(e) =>
                      handleInputChange("sessionRate", e.target.value)
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Typical session duration
                </label>
                <select
                  value={formData.sessionDuration}
                  onChange={(e) =>
                    handleInputChange("sessionDuration", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    color: formData.sessionDuration ? "#1a1a1a" : "#a3a3a3",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    outline: "none",
                    cursor: "pointer",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#a3a3a3")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e5e5e5")
                  }
                >
                  <option value="">Select duration</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">2 hours</option>
                  <option value="240">Half day (4 hours)</option>
                  <option value="480">Full day (8 hours)</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  This rate includes (select all that apply)
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {[
                    {
                      id: "travelIncluded",
                      label: "Travel within service radius",
                    },
                    { id: "equipmentIncluded", label: "Basic equipment/setup" },
                    { id: "preparationIncluded", label: "Preparation time" },
                    { id: "qaIncluded", label: "Q&A session" },
                  ].map((item) => (
                    <label
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        color: "#525252",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.rateIncludes?.[item.id] || false}
                        onChange={(e) => {
                          const newIncludes = {
                            ...formData.rateIncludes,
                            [item.id]: e.target.checked,
                          };
                          handleInputChange("rateIncludes", newIncludes);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  Willing to negotiate pricing?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() => handleInputChange("negotiable", true)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.negotiable === true ? "#000" : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.negotiable === true
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.negotiable !== true) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.negotiable !== true) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    Yes, open to discussion
                  </button>
                  <button
                    onClick={() => handleInputChange("negotiable", false)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.negotiable === false ? "#000" : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.negotiable === false
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.negotiable !== false) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.negotiable !== false) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    No, rate is fixed
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Email Marketing Consent */}
          <div
            style={{
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: "1px solid #e5e5e5",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={formData.marketingConsent}
                onChange={(e) =>
                  handleInputChange("marketingConsent", e.target.checked)
                }
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "15px",
                  color: "#1a1a1a",
                  lineHeight: "1.5",
                }}
              >
                Email me about new features and platform updates
              </span>
            </label>
            <p
              style={{
                fontSize: "13px",
                color: "#737373",
                marginTop: "8px",
                marginLeft: "30px",
                lineHeight: "1.5",
              }}
            >
              You'll always receive important notifications about bookings and
              messages regardless of this setting
            </p>
          </div>

          <button
            disabled={!isStep5Valid}
            onClick={handleMusicianSubmit}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep5Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep5Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep5Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep5Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep5Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Submit
          </button>
        </div>
      </div>
    );
  }
  //Steps Other:
  // Step 1: Personal Details (Other)
  if (step === "other-details") {
    // Copy the entire speaker-details block and just change step === 'speaker-details' to step === 'musician-details'
    // Everything else stays the same
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={1} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            {editMode ? "Update your details" : "Tell us a bit more about you"}
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Profile Picture
              </label>
              <p
                style={{
                  fontSize: "13px",
                  color: "#737373",
                  margin: "0 0 8px 0",
                }}
              >
                Upload a professional photo (max 5MB)
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                {(imagePreview || formData.profilePictureUrl) && (
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "2px solid #e5e5e5",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={imagePreview || formData.profilePictureUrl}
                      alt="Profile preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
                <label
                  style={{
                    display: "inline-block",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: uploadingImage ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: uploadingImage ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#a3a3a3";
                      e.currentTarget.style.backgroundColor = "#fafafa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  {uploadingImage ? "Uploading..." : "Choose Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <TextInput
              label="Your Church"
              value={formData.church}
              onChange={(e) => handleInputChange("church", e.target.value)}
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Tell us about you
              </label>
              <p
                style={{
                  fontSize: "13px",
                  color: "#737373",
                  margin: "0 0 8px 0",
                }}
              >
                Share your qualifications and experience
              </p>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
                rows={4}
                placeholder="Tell us about your background, qualifications, and experience..."
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div>
          </div>

          <button
            disabled={!isStep1Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep1Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep1Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep1Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Travel (Other)
  if (step === "other-travel") {
    // Copy the entire speaker-travel block and just change step === 'speaker-travel' to step === 'musician-travel'
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={3} totalSteps={5} />

          <FormHeader title="Travel" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Your city or town"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              placeholder="Enter your city or town"
            />

            {/* Travel Method Selection */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                }}
              >
                How do you typically travel to engagements?
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                {[
                  { value: "car", label: "Car" },
                  { value: "train", label: "Train" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleTravelMethodToggle(option.value)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.travelMethod.includes(option.value)
                        ? "#000"
                        : "#525252",
                      backgroundColor: "#fff",
                      border: formData.travelMethod.includes(option.value)
                        ? "2px solid #000"
                        : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "left",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Car Travel Fields - Conditional */}
            {formData.travelMethod.includes("car") && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#525252",
                      marginBottom: "12px",
                    }}
                  >
                    I prefer to set my car travel limit by:
                  </label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    {[
                      { value: "distance", label: "Distance" },
                      { value: "time", label: "Time" },
                    ].map((option) => (
                      <label
                        key={option.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "15px",
                          color: "#525252",
                        }}
                      >
                        <input
                          type="radio"
                          name="carLimitType"
                          value={option.value}
                          checked={formData.carLimitType === option.value}
                          onChange={(e) =>
                            handleInputChange("carLimitType", e.target.value)
                          }
                          style={{ cursor: "pointer" }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                {formData.carLimitType === "distance" && (
                  <SelectDropdown
                    label="Maximum distance willing to travel by car"
                    value={formData.maxCarDistance}
                    onChange={(e) =>
                      handleInputChange("maxCarDistance", e.target.value)
                    }
                    placeholder="Select distance"
                    options={[
                      { value: "10 miles", label: "10 miles" },
                      { value: "25 miles", label: "25 miles" },
                      { value: "50 miles", label: "50 miles" },
                      { value: "75 miles", label: "75 miles" },
                      { value: "100 miles", label: "100 miles" },
                      { value: "150+ miles", label: "150+ miles" },
                      { value: "Flexible", label: "Flexible" },
                    ]}
                  />
                )}

                {formData.carLimitType === "time" && (
                  <SelectDropdown
                    label="Maximum driving time"
                    value={formData.maxCarTime}
                    onChange={(e) =>
                      handleInputChange("maxCarTime", e.target.value)
                    }
                    placeholder="Select time"
                    options={[
                      { value: "30 minutes", label: "30 minutes" },
                      { value: "1 hour", label: "1 hour" },
                      { value: "1.5 hours", label: "1.5 hours" },
                      { value: "2 hours", label: "2 hours" },
                      { value: "2.5 hours", label: "2.5 hours" },
                      { value: "3+ hours", label: "3+ hours" },
                      { value: "Flexible", label: "Flexible" },
                    ]}
                  />
                )}
              </>
            )}

            {/* Train Travel Fields - Conditional */}
            {formData.travelMethod.includes("train") && (
              <>
                <SelectDropdown
                  label="Maximum train journey time"
                  value={formData.maxTrainTime}
                  onChange={(e) =>
                    handleInputChange("maxTrainTime", e.target.value)
                  }
                  placeholder="Select time"
                  options={[
                    { value: "1 hour", label: "1 hour" },
                    { value: "1.5 hours", label: "1.5 hours" },
                    { value: "2 hours", label: "2 hours" },
                    { value: "2.5 hours", label: "2.5 hours" },
                    { value: "3+ hours", label: "3+ hours" },
                    { value: "Flexible", label: "Flexible" },
                  ]}
                />

                <SelectDropdown
                  label="Maximum number of train changes"
                  value={formData.maxTrainChanges}
                  onChange={(e) =>
                    handleInputChange("maxTrainChanges", e.target.value)
                  }
                  placeholder="Select changes"
                  options={[
                    { value: "Direct only", label: "Direct only" },
                    { value: "1 change", label: "1 change" },
                    { value: "2 changes", label: "2 changes" },
                    { value: "3+ changes", label: "3+ changes" },
                    { value: "No preference", label: "No preference" },
                  ]}
                />
              </>
            )}

            <TextArea
              label="Any other considerations?"
              optional={true}
              value={formData.otherConsiderations}
              onChange={(e) =>
                handleInputChange("otherConsiderations", e.target.value)
              }
              rows={4}
            />
          </div>

          <button
            disabled={!isStep3Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep3Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep3Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep3Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep3Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep3Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Availability (Other)
  if (step === "other-availability") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={4} totalSteps={5} />

          <FormHeader title="When can churches book you?" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                }}
              >
                General availability
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                }}
              >
                {[
                  { value: "weekdays", label: "Weekdays" },
                  { value: "weekends", label: "Weekends" },
                  { value: "evenings", label: "Evenings" },
                  { value: "weekday_evenings", label: "Weekday evenings" },
                  { value: "sundays", label: "Sunday services" },
                  { value: "flexible", label: "Very flexible" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() =>
                      handleInputChange("availabilityType", option.value)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.availabilityType === option.value
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.availabilityType === option.value
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.availabilityType !== option.value) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.availabilityType !== option.value) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <SelectDropdown
                label="Minimum advance notice"
                value={formData.minAdvanceNoticeDays}
                onChange={(e) =>
                  handleInputChange("minAdvanceNoticeDays", e.target.value)
                }
                helperText="How much notice do you need before a booking?"
                placeholder="Select notice period"
                options={[
                  { value: "0", label: "Same day (if available)" },
                  { value: "1", label: "24 hours" },
                  { value: "2", label: "48 hours" },
                  { value: "3", label: "3 days" },
                  { value: "7", label: "1 week" },
                  { value: "14", label: "2 weeks" },
                  { value: "30", label: "1 month" },
                ]}
              />
            </div>

            <div>
              <TextArea
                label="Additional availability notes"
                value={formData.availabilityNotes}
                onChange={(e) =>
                  handleInputChange("availabilityNotes", e.target.value)
                }
                rows={4}
                placeholder="e.g., 'Available most weekday evenings after 6 PM. Unavailable July-August (summer break). Prefer morning sessions but flexible for special events.'"
                helperText="Share any specific availability details, blackout periods, or preferences"
                optional={true}
              />
            </div>
          </div>

          <button
            disabled={!isStep4Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep4Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep4Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep4Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep4Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep4Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }
  // Step 5: Pricing (Speaker)
  if (step === "other-pricing") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <FormNavigation
            onBack={handleBack}
            onSaveAndExit={handleSaveAndExit}
          />

          <StepIndicator currentStep={5} totalSteps={5} />

          <FormHeader title="How would you like to offer your services?" />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "32px",
            }}
          >
            <button
              onClick={() => handleInputChange("pricingModel", "free")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color: formData.pricingModel === "free" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "free"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "free") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "free") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Free Ministry
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I want to serve freely, including covering my own costs
              </div>
            </button>

            <button
              onClick={() => handleInputChange("pricingModel", "flexible")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color:
                  formData.pricingModel === "flexible" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "flexible"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "flexible") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "flexible") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Flexible / Expense-Based
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I'm open to discussion based on church budget and needs
              </div>
            </button>

            <button
              onClick={() => handleInputChange("pricingModel", "set_rate")}
              style={{
                padding: "20px 24px",
                fontSize: "16px",
                fontWeight: "400",
                color:
                  formData.pricingModel === "set_rate" ? "#000" : "#525252",
                backgroundColor: "#fff",
                border:
                  formData.pricingModel === "set_rate"
                    ? "2px solid #000"
                    : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                outline: "none",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                if (formData.pricingModel !== "set_rate") {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                }
              }}
              onMouseLeave={(e) => {
                if (formData.pricingModel !== "set_rate") {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                }
              }}
            >
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Set Rate
              </div>
              <div style={{ fontSize: "14px", color: "#737373" }}>
                I charge a standard rate for my services
              </div>
            </button>
          </div>

          {/* Flexible Pricing Options */}
          {formData.pricingModel === "flexible" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "32px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  What expenses would you need covered?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {[
                    { id: "travel", label: "Travel costs" },
                    {
                      id: "accommodation",
                      label: "Accommodation (if required)",
                    },
                    { id: "equipment", label: "Equipment/materials" },
                    { id: "meals", label: "Meal allowance" },
                  ].map((expense) => (
                    <label
                      key={expense.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        color: "#525252",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.expensesNeeded?.[expense.id] || false}
                        onChange={(e) => {
                          const newExpenses = {
                            ...formData.expensesNeeded,
                            [expense.id]: e.target.checked,
                          };
                          handleInputChange("expensesNeeded", newExpenses);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span>{expense.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  Would you accept an honorarium if offered?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() =>
                      handleInputChange("honorariumAccepted", true)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.honorariumAccepted === true
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.honorariumAccepted === true
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.honorariumAccepted !== true) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.honorariumAccepted !== true) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    Yes, open to honorarium
                  </button>
                  <button
                    onClick={() =>
                      handleInputChange("honorariumAccepted", false)
                    }
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color:
                        formData.honorariumAccepted === false
                          ? "#000"
                          : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.honorariumAccepted === false
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.honorariumAccepted !== false) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.honorariumAccepted !== false) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    No, expenses only
                  </button>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Suggested contribution{" "}
                  <span style={{ color: "#a3a3a3", fontWeight: "400" }}>
                    (optional)
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#737373",
                    margin: "0 0 8px 0",
                  }}
                >
                  Churches often ask for guidance on appropriate amounts
                </p>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.suggestedContribution}
                    onChange={(e) =>
                      handleInputChange("suggestedContribution", e.target.value)
                    }
                    placeholder="e.g., 100, 200"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div>

              {/* <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  If budget allows, minimum acceptable{" "}
                  <span style={{ color: "#a3a3a3", fontWeight: "400" }}>
                    (optional)
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#737373",
                    margin: "0 0 8px 0",
                  }}
                >
                  Sets a floor for what you can accept if charging
                </p>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.minimumRate}
                    onChange={(e) =>
                      handleInputChange("minimumRate", e.target.value)
                    }
                    placeholder="e.g., 75, 100"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div> */}
            </div>
          )}

          {/* Set Rate Options */}
          {formData.pricingModel === "set_rate" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "32px",
              }}
            >
              {/* <div
                style={{
                  textAlign: "center",
                  marginBottom: "12px",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      "Pricing tips:\n\n• Research rates in your area\n• Factor in preparation time\n• Include travel costs within your radius\n• Account for platform fees\n• Consider your experience level\n\nTypical rates:\n• Entry level: £50-150/session\n• Experienced: £150-400/session\n• Expert: £400+/session"
                    )
                  }
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips on pricing
                </button>
              </div> */}

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Typical rate per session
                </label>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "15px",
                      color: "#525252",
                    }}
                  >
                    £
                  </span>
                  <input
                    type="number"
                    value={formData.sessionRate}
                    onChange={(e) =>
                      handleInputChange("sessionRate", e.target.value)
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 28px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                </div>
              </div>

              {/* <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Typical session duration
                </label>
                <select
                  value={formData.sessionDuration}
                  onChange={(e) =>
                    handleInputChange("sessionDuration", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    fontSize: "15px",
                    color: formData.sessionDuration ? "#1a1a1a" : "#a3a3a3",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    outline: "none",
                    cursor: "pointer",
                    transition: "border-color 0.2s ease",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#a3a3a3")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e5e5e5")
                  }
                >
                  <option value="">Select duration</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">2 hours</option>
                  <option value="240">Half day (4 hours)</option>
                  <option value="480">Full day (8 hours)</option>
                </select>
              </div> */}

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  This rate includes (select all that apply)
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {[
                    {
                      id: "travelIncluded",
                      label: "Travel within service radius",
                    },
                    { id: "equipmentIncluded", label: "Basic equipment/setup" },
                    { id: "preparationIncluded", label: "Preparation time" },
                    { id: "qaIncluded", label: "Q&A session" },
                  ].map((item) => (
                    <label
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "12px 16px",
                        fontSize: "15px",
                        color: "#525252",
                        backgroundColor: "#fff",
                        border: "1px solid #e5e5e5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.rateIncludes?.[item.id] || false}
                        onChange={(e) => {
                          const newIncludes = {
                            ...formData.rateIncludes,
                            [item.id]: e.target.checked,
                          };
                          handleInputChange("rateIncludes", newIncludes);
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "12px",
                  }}
                >
                  Willing to negotiate pricing?
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={() => handleInputChange("negotiable", true)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.negotiable === true ? "#000" : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.negotiable === true
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.negotiable !== true) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.negotiable !== true) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    Yes, open to discussion
                  </button>
                  <button
                    onClick={() => handleInputChange("negotiable", false)}
                    style={{
                      padding: "14px 18px",
                      fontSize: "15px",
                      fontWeight: "400",
                      color: formData.negotiable === false ? "#000" : "#525252",
                      backgroundColor: "#fff",
                      border:
                        formData.negotiable === false
                          ? "2px solid #000"
                          : "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (formData.negotiable !== false) {
                        e.currentTarget.style.borderColor = "#a3a3a3";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.negotiable !== false) {
                        e.currentTarget.style.borderColor = "#e5e5e5";
                      }
                    }}
                  >
                    No, rate is fixed
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Email Marketing Consent */}
          <div
            style={{
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: "1px solid #e5e5e5",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={formData.marketingConsent}
                onChange={(e) =>
                  handleInputChange("marketingConsent", e.target.checked)
                }
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: "15px",
                  color: "#1a1a1a",
                  lineHeight: "1.5",
                }}
              >
                Email me about new features and platform updates
              </span>
            </label>
            <p
              style={{
                fontSize: "13px",
                color: "#737373",
                marginTop: "8px",
                marginLeft: "30px",
                lineHeight: "1.5",
              }}
            >
              You'll always receive important notifications about bookings and
              messages regardless of this setting
            </p>
          </div>

          <button
            disabled={!isStep5Valid}
            onClick={handleOtherSubmit}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep5Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep5Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep5Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep5Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep5Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  // Unified details step (Step 1 - Personal Information)
  if (step === "details") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={1} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            {editMode ? "Update your details" : "Tell us a bit more about you"}
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Profile Picture
              </label>
              <p
                style={{
                  fontSize: "13px",
                  color: "#737373",
                  margin: "0 0 8px 0",
                }}
              >
                Upload a professional photo (max 5MB)
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                {(imagePreview || formData.profilePictureUrl) && (
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "2px solid #e5e5e5",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={imagePreview || formData.profilePictureUrl}
                      alt="Profile preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
                <label
                  style={{
                    display: "inline-block",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: uploadingImage ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: uploadingImage ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#a3a3a3";
                      e.currentTarget.style.backgroundColor = "#fafafa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  {uploadingImage ? "Uploading..." : "Choose Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <TextInput
              label="Your church name"
              value={formData.church}
              onChange={(e) => handleInputChange("church", e.target.value)}
            />

            <TextInput
              label="Church city/town"
              value={formData.churchCity}
              onChange={(e) => handleInputChange("churchCity", e.target.value)}
              placeholder="e.g. London, Manchester, Birmingham"
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Denomination
              </label>
              <select
                value={formData.denomination}
                onChange={(e) => handleInputChange("denomination", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  cursor: "pointer",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              >
                <option value="">Select a denomination</option>
                <option value="Anglican (CoE)">Anglican (CoE)</option>
                <option value="Assemblies of God">Assemblies of God</option>
                <option value="Baptist">Baptist</option>
                <option value="Catholic">Catholic</option>
                <option value="Elim">Elim</option>
                <option value="FIEC">FIEC</option>
                <option value="Independent">Independent</option>
                <option value="Methodist">Methodist</option>
                <option value="New Frontiers">New Frontiers</option>
                <option value="Pentecostal">Pentecostal</option>
                <option value="Salvation Army">Salvation Army</option>
                <option value="URC">URC</option>
                <option value="Vineyard">Vineyard</option>
                <option value="Other">Other (please specify in 'church name' field)</option>
              </select>
            </div>

            <TextArea
              label="Qualifications"
              helperText="Share your relevant qualifications, certifications, training, or education"
              value={formData.qualifications}
              onChange={(e) => handleInputChange("qualifications", e.target.value)}
              rows={3}
              placeholder="e.g. BTh, MTh, Ordained Minister, DBS checked, etc."
            />

            <TextArea
              label="Tell us about you"
              helperText="Share your background and experience"
              value={formData.bio}
              onChange={(e) => handleInputChange("bio", e.target.value)}
              rows={4}
              placeholder="Share a brief introduction - your background, experience, or what you're passionate about (2-3 sentences)"
            />
          </div>

          <button
            disabled={!isStep1Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep1Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep1Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep1Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Unified service-info step (Step 2 - Service Information with conditional expertise fields)
  if (step === "service-info") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={2} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            Give your service a title
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {/* Service Title - Always shown */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                  }}
                >
                  Service Title
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const tips = [];
                    if (selectedTypes.includes("Speaker")) {
                      tips.push('• "Youth Ministry Leadership"');
                      tips.push('• "Marriage & Family Counseling"');
                      tips.push('• "Biblical Teaching for Small Groups"');
                    }
                    if (selectedTypes.includes("Musician")) {
                      tips.push('• "Worship Band Leader"');
                      tips.push('• "Classical Piano Performance"');
                      tips.push('• "Contemporary Worship Guitar"');
                    }
                    if (selectedTypes.includes("Other")) {
                      tips.push('• "Event Planning & Coordination"');
                      tips.push('• "Youth Program Development"');
                      tips.push('• "Community Outreach Services"');
                    }
                    alert(
                      "Tips: Keep it clear and descriptive. Examples:\n" +
                        tips.join("\n")
                    );
                  }}
                  style={{
                    padding: "4px 12px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "transparent",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#a3a3a3";
                    e.currentTarget.style.color = "#1a1a1a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e5e5";
                    e.currentTarget.style.color = "#525252";
                  }}
                >
                  Get tips
                </button>
              </div>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              />
            </div>

            {/* Topics for your talks - Only shown for Speakers */}
            {selectedTypes.includes("Speaker") && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  Topics for your talks (expertise areas)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={currentTopic}
                    onChange={(e) => setCurrentTopic(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type and press Add"
                    style={{
                      flex: 1,
                      padding: "12px 16px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                  <button
                    onClick={addTopic}
                    disabled={!currentTopic.trim()}
                    style={{
                      padding: "12px 20px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: currentTopic.trim() ? "#000" : "#a3a3a3",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: currentTopic.trim() ? "pointer" : "not-allowed",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => {
                      if (currentTopic.trim())
                        e.currentTarget.style.borderColor = "#a3a3a3";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                    }}
                  >
                    Add
                  </button>
                </div>
                {formData.topics.length > 0 && (
                  <div
                    style={{
                      marginTop: "12px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {formData.topics.map((topic, index) => (
                      <div
                        key={index}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          fontSize: "14px",
                          color: "#1a1a1a",
                          backgroundColor: "#fff",
                          border: "1px solid #e5e5e5",
                          borderRadius: "6px",
                        }}
                      >
                        {topic}
                        <button
                          onClick={() => removeTopic(topic)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#737373",
                            cursor: "pointer",
                            padding: "0",
                            fontSize: "18px",
                            lineHeight: "1",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#1a1a1a")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#737373")
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Instruments - Only shown for Musicians */}
            {selectedTypes.includes("Musician") && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    marginBottom: "8px",
                  }}
                >
                  What instruments do you play? (expertise areas)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="text"
                    value={currentInstrument}
                    onChange={(e) => setCurrentInstrument(e.target.value)}
                    onKeyPress={handleInstrumentKeyPress}
                    placeholder="Type and press Add"
                    style={{
                      flex: 1,
                      padding: "12px 16px",
                      fontSize: "15px",
                      color: "#1a1a1a",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#a3a3a3")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "#e5e5e5")
                    }
                  />
                  <button
                    onClick={addInstrument}
                    disabled={!currentInstrument.trim()}
                    style={{
                      padding: "12px 20px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: currentInstrument.trim() ? "#000" : "#a3a3a3",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      cursor: currentInstrument.trim()
                        ? "pointer"
                        : "not-allowed",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => {
                      if (currentInstrument.trim())
                        e.currentTarget.style.borderColor = "#a3a3a3";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                    }}
                  >
                    Add
                  </button>
                </div>
                {formData.instruments.length > 0 && (
                  <div
                    style={{
                      marginTop: "12px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {formData.instruments.map((instrument, index) => (
                      <div
                        key={index}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          fontSize: "14px",
                          color: "#1a1a1a",
                          backgroundColor: "#fff",
                          border: "1px solid #e5e5e5",
                          borderRadius: "6px",
                        }}
                      >
                        {instrument}
                        <button
                          onClick={() => removeInstrument(instrument)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#737373",
                            cursor: "pointer",
                            padding: "0",
                            fontSize: "18px",
                            lineHeight: "1",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#1a1a1a")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#737373")
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description - Always shown */}
            <TextArea
              label="Describe your service"
              optional={true}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={4}
            />

            {/* Age Groups - Always shown */}
            <AgeGroupSelector
              label="Who is your ministry for?"
              helperText="Select all age groups that apply"
              value={formData.ageGroups}
              onChange={(newValue) => handleInputChange("ageGroups", newValue)}
            />

            {/* Service Availability - Online/In-Person */}
            <div>
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "12px",
                  display: "block",
                }}
              >
                How do you offer your service?
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px",
                    backgroundColor: "#fff",
                    border: `2px solid ${formData.onlineAvailable ? "#000" : "#e5e5e5"}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.onlineAvailable) {
                      e.currentTarget.style.borderColor = "#a3a3a3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formData.onlineAvailable) {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.onlineAvailable}
                    onChange={(e) =>
                      handleInputChange("onlineAvailable", e.target.checked)
                    }
                    style={{
                      width: "18px",
                      height: "18px",
                      marginRight: "12px",
                      cursor: "pointer",
                    }}
                  />
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: "500", color: "#1a1a1a" }}>
                      Online (Remote)
                    </div>
                    <div style={{ fontSize: "13px", color: "#737373", marginTop: "2px" }}>
                      Available via video call or virtual platforms
                    </div>
                  </div>
                </label>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px",
                    backgroundColor: "#fff",
                    border: `2px solid ${formData.inPersonAvailable ? "#000" : "#e5e5e5"}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!formData.inPersonAvailable) {
                      e.currentTarget.style.borderColor = "#a3a3a3";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!formData.inPersonAvailable) {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.inPersonAvailable}
                    onChange={(e) =>
                      handleInputChange("inPersonAvailable", e.target.checked)
                    }
                    style={{
                      width: "18px",
                      height: "18px",
                      marginRight: "12px",
                      cursor: "pointer",
                    }}
                  />
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: "500", color: "#1a1a1a" }}>
                      In-Person
                    </div>
                    <div style={{ fontSize: "13px", color: "#737373", marginTop: "2px" }}>
                      Available to meet and serve at physical locations
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <button
            disabled={!formData.title}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: formData.title ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: formData.title ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: formData.title ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (formData.title) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (formData.title) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (step === "speaker-details") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Navigation Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            {/* Back Button */}
            <button
              onClick={handleBack}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Back
            </button>

            {/* Save & Exit Button */}
            <button
              onClick={handleSaveAndExit}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                backgroundColor: "transparent",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#a3a3a3";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e5e5";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Save & Exit
            </button>
          </div>

          <StepIndicator currentStep={1} totalSteps={5} />

          <h1
            style={{
              fontSize: "24px",
              fontWeight: "500",
              color: "#1a1a1a",
              marginBottom: "40px",
              textAlign: "center",
              letterSpacing: "-0.01em",
            }}
          >
            {editMode ? "Update your details" : "Tell us a bit more about you"}
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <TextInput
              label="Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Profile Picture
              </label>
              <p
                style={{
                  fontSize: "13px",
                  color: "#737373",
                  margin: "0 0 8px 0",
                }}
              >
                Upload a professional photo (max 5MB)
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                {(imagePreview || formData.profilePictureUrl) && (
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "2px solid #e5e5e5",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={imagePreview || formData.profilePictureUrl}
                      alt="Profile preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                )}
                <label
                  style={{
                    display: "inline-block",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#525252",
                    backgroundColor: "#fff",
                    border: "1px solid #e5e5e5",
                    borderRadius: "6px",
                    cursor: uploadingImage ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: uploadingImage ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#a3a3a3";
                      e.currentTarget.style.backgroundColor = "#fafafa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploadingImage) {
                      e.currentTarget.style.borderColor = "#e5e5e5";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  {uploadingImage ? "Uploading..." : "Choose Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <TextInput
              label="Your church name"
              value={formData.church}
              onChange={(e) => handleInputChange("church", e.target.value)}
            />

            <TextInput
              label="Church city/town"
              value={formData.churchCity}
              onChange={(e) => handleInputChange("churchCity", e.target.value)}
              placeholder="e.g. London, Manchester, Birmingham"
            />

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#525252",
                  marginBottom: "8px",
                }}
              >
                Denomination
              </label>
              <select
                value={formData.denomination}
                onChange={(e) => handleInputChange("denomination", e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "15px",
                  color: "#1a1a1a",
                  backgroundColor: "#fff",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  cursor: "pointer",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
              >
                <option value="">Select a denomination</option>
                <option value="Anglican (CoE)">Anglican (CoE)</option>
                <option value="Assemblies of God">Assemblies of God</option>
                <option value="Baptist">Baptist</option>
                <option value="Catholic">Catholic</option>
                <option value="Elim">Elim</option>
                <option value="FIEC">FIEC</option>
                <option value="Independent">Independent</option>
                <option value="Methodist">Methodist</option>
                <option value="New Frontiers">New Frontiers</option>
                <option value="Pentecostal">Pentecostal</option>
                <option value="Salvation Army">Salvation Army</option>
                <option value="URC">URC</option>
                <option value="Vineyard">Vineyard</option>
                <option value="Other">Other (please specify in 'church name' field)</option>
              </select>
            </div>

            <TextArea
              label="Qualifications"
              helperText="Share your relevant qualifications, certifications, training, or education"
              value={formData.qualifications}
              onChange={(e) => handleInputChange("qualifications", e.target.value)}
              rows={3}
              placeholder="e.g. BTh, MTh, Ordained Minister, DBS checked, etc."
            />

            <TextArea
              label="Tell us about you"
              helperText="Share your background and experience"
              value={formData.bio}
              onChange={(e) => handleInputChange("bio", e.target.value)}
              rows={4}
              placeholder="Share a brief introduction - your background, experience, or what you're passionate about (2-3 sentences)"
            />
          </div>

          <button
            disabled={!isStep1Valid}
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: isStep1Valid ? "#000" : "#e5e5e5",
              border: "none",
              borderRadius: "8px",
              cursor: isStep1Valid ? "pointer" : "not-allowed",
              transition: "opacity 0.2s ease",
              opacity: isStep1Valid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "0.85";
            }}
            onMouseLeave={(e) => {
              if (isStep1Valid) e.currentTarget.style.opacity = "1";
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ADD THIS LOADING SCREEN:
  if (loading && editMode) {
    return (
      <div style={styles.container}>
        <p style={{ fontSize: "15px", color: "#525252" }}>
          Loading your profile...
        </p>
      </div>
    );
  }

  // Initial service selection
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Save & Exit Only */}
        {/* <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "24px",
          }}
        >
          <button
            onClick={handleSaveAndExit}
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#525252",
              backgroundColor: "transparent",
              border: "1px solid #e5e5e5",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#a3a3a3";
              e.currentTarget.style.backgroundColor = "#fafafa";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e5e5";
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Save & Exit
          </button>
        </div> */}
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "500",
            color: "#1a1a1a",
            marginBottom: "16px",
            textAlign: "center",
            letterSpacing: "-0.01em",
          }}
        >
          What type(s) of service will you provide?
        </h1>

        <p
          style={{
            fontSize: "14px",
            color: "#737373",
            marginBottom: "32px",
            textAlign: "center",
          }}
        >
          Select all that apply
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {options.map((option) => (
            <label
              key={option}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 20px",
                backgroundColor: selectedTypes.includes(option)
                  ? "#fafafa"
                  : "#fff",
                border: selectedTypes.includes(option)
                  ? "2px solid #1a1a1a"
                  : "1px solid #e5e5e5",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                if (!selectedTypes.includes(option)) {
                  e.currentTarget.style.borderColor = "#a3a3a3";
                  e.currentTarget.style.backgroundColor = "#fafafa";
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedTypes.includes(option)) {
                  e.currentTarget.style.borderColor = "#e5e5e5";
                  e.currentTarget.style.backgroundColor = "#fff";
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedTypes.includes(option)}
                onChange={() => handleServiceTypeToggle(option)}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  accentColor: "#1a1a1a",
                }}
              />
              <span
                style={{
                  fontSize: "16px",
                  color: "#1a1a1a",
                  fontWeight: selectedTypes.includes(option) ? "500" : "400",
                }}
              >
                {option}
              </span>
            </label>
          ))}
        </div>

        {/* Custom service type input - shown when "Other" is selected */}
        {selectedTypes.includes("Other") && (
          <div style={{ marginTop: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#525252",
                marginBottom: "8px",
              }}
            >
              Please specify your service type
            </label>
            <input
              type="text"
              value={formData.customServiceType}
              onChange={(e) => handleInputChange("customServiceType", e.target.value)}
              placeholder="e.g. Youth Worker, Counsellor, Event Organiser, etc."
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: "15px",
                color: "#1a1a1a",
                backgroundColor: "#fff",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#a3a3a3")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e5e5")}
            />
          </div>
        )}

        {selectedTypes.length > 0 && (
          <button
            onClick={handleContinue}
            style={{
              marginTop: "32px",
              width: "100%",
              padding: "16px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: "#000",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Continue
          </button>
        )}

        {/* Exit to Browse Services */}
        <button
          onClick={() => (window.location.href = "/explore")}
          style={{
            marginTop: "16px",
            width: "100%",
            padding: "12px",
            fontSize: "14px",
            fontWeight: "500",
            color: "#737373",
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#525252";
            e.currentTarget.style.backgroundColor = "#fafafa";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#737373";
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          Exit to Browse Services
        </button>
      </div>
    </div>
  );
}

// OnboardingMVP-inspired styles
const styles = {
  container: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: "#fafafa",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
  },
  card: {
    maxWidth: "700px",
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #e5e5e5",
    padding: "3rem",
  },
  formGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    display: "block",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    marginBottom: "0.5rem",
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "15px",
    border: "1px solid #e5e5e5",
    borderRadius: "8px",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease",
  },
  helperText: {
    fontSize: "13px",
    color: "#737373",
    marginTop: "0.25rem",
  },
  error: {
    fontSize: "13px",
    color: "#dc2626",
    marginTop: "0.25rem",
  },
};
